import { type FC } from "react";
import {
  Avatar,
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUserQuery } from "@/partner/entities/users";
import { getInitials } from "@/partner/shared/utils";
import { DialogHeader } from "@/shared/components";
import { AttributionTouchList } from "@/shared/components/AttributionTouch";
import { interpolationFormat } from "@/partner/shared/lib";

interface UserDialogProps {
  open: boolean;
  userId: string | null;
  onClose: () => void;
}

const NEWSLETTER_LABEL_KEYS: Record<"yes" | "no", string> = {
  yes: "common.yes",
  no: "common.no",
};

export const UserDialog: FC<UserDialogProps> = ({ open, userId, onClose }) => {
  const { t, i18n } = useTranslation();
  const { data, loading, error } = useUserQuery({
    variables: { id: String(userId) },
    skip: !userId || !open,
    fetchPolicy: "network-only",
  });

  const user = data?.user;
  const formatDate = (iso: string) =>
    interpolationFormat(iso, "dateTime", i18n.language);
  const title =
    user?.displayName || user?.email || t("users.dialog.fallbackTitle");

  return (
    <Dialog fullWidth maxWidth="md" scroll="body" open={open} onClose={onClose}>
      <DialogHeader title={title} loading={loading} onClose={onClose} />
      <DialogContent sx={{ p: 1 }}>
        <Stack spacing={1}>
          <Paper sx={{ p: 2 }}>
            {error && <Typography color="error">{error.message}</Typography>}
            {user && (
              <Grid container spacing={2}>
                <Grid>
                  <Avatar
                    src={user.avatar?.url ?? undefined}
                    sx={{ width: 64, height: 64, bgcolor: "primary.main" }}
                  >
                    {getInitials(user as Parameters<typeof getInitials>[0])}
                  </Avatar>
                </Grid>
                <Grid>
                  <Stack spacing={2}>
                    {user.email && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("users.dialog.emailLabel")}
                        </Typography>
                        <Typography variant="body1">{user.email}</Typography>
                      </Box>
                    )}
                    {(user.firstName || user.lastName) && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("users.dialog.nameLabel")}
                        </Typography>
                        <Typography variant="body1">
                          {[user.firstName, user.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </Typography>
                      </Box>
                    )}
                    {user.language && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("users.dialog.languageLabel")}
                        </Typography>
                        <Typography variant="body1">{user.language}</Typography>
                      </Box>
                    )}
                    {user.country && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("users.dialog.countryLabel")}
                        </Typography>
                        <Typography variant="body1">{user.country}</Typography>
                      </Box>
                    )}
                    {user.timeZone && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("users.dialog.timeZoneLabel")}
                        </Typography>
                        <Typography variant="body1">{user.timeZone}</Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("users.dialog.newsletterLabel")}
                      </Typography>
                      <Typography variant="body1">
                        {t(
                          NEWSLETTER_LABEL_KEYS[
                            user.agreeToMarketingNewsletters ? "yes" : "no"
                          ]
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Paper>
          {user && (
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("users.dialog.registrationTouchTitle")}
                  </Typography>
                  <AttributionTouchList
                    touch={user.registrationUtm}
                    formatDate={formatDate}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("users.dialog.lastTouchTitle")}
                  </Typography>
                  <AttributionTouchList
                    touch={user.lastUtm}
                    formatDate={formatDate}
                  />
                  {user.marketingConsent && (
                    <Typography variant="caption" color="text.secondary">
                      {`${t("users.dialog.marketingConsentAt")}: ${formatDate(user.marketingConsent.at)}`}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
